import { useMutation, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { ApiClient } from '../../../../api/ApiClient';
import { v2 } from '../../../../api/ApiInterfaceV2';
import { Button, Prompt } from '../../../../ui';
import { ResultInfoForMutation } from '../../../../ui/ResultInfo';
import { useResolve } from '../../../Container';
import { Popup, PopupProps } from '../../../popups/Popup';
import { Copy, Em, Spacer } from '../../elements/LayoutElements';

export const templateDeletePopup = (templateId: string) => ({ ui: TemplateDeletePopup, args: { templateId } });

export const TemplateDeletePopup = ({ action, dismiss, args }: PopupProps) => {
    const { templateId } = args;

    const api = useResolve(ApiClient);
    const queries = useQueryClient();
    const deleter = useMutation({
        mutationFn: () => api.exec(v2.templates.delete(templateId)),
        onSuccess() {
            queries.invalidateQueries(['templates.list']);
            action(true);
        }
    });

    const onDelete = () => void (deleter.mutate());

    const onPromptAction = (confirm: boolean) => {
        if (!confirm) dismiss();
        else onDelete();
    };

    return <Prompt onAction={deleter.isLoading ? null : onPromptAction}>
        <Popup w={32}>
            <Popup.Close onAction={() => onPromptAction(false)} />
            <Popup.Title>Delete Template</Popup.Title>
            <Popup.Content>
                <Copy>
                    Are you sure you want to <Em>permanently delete</Em> this template?  This cannot be undone.
                </Copy>
                <Spacer f={2} />
                <ResultInfoForMutation mutation={deleter} />
            </Popup.Content>
            <Popup.Actions>
                <Button disabled={deleter.isLoading} variant="negative" onAction={onDelete}>
                    {deleter.isLoading ? 'Deleting...' : 'Delete'}
                </Button>
                <Button disabled={deleter.isLoading} onAction={dismiss}>
                    Cancel
                </Button>
            </Popup.Actions>
        </Popup>
    </Prompt>;
};
